/**
 * motion.ts — single source of truth for GSAP setup on Good Atoms.
 *
 * Responsibilities:
 *  - Register GSAP plugins exactly once per page load
 *  - Expose a `motionEnabled` flag tied to `prefers-reduced-motion`
 *  - Provide standard easing + duration tokens that mirror CLAUDE.md
 *
 * Anything that uses GSAP should import from here — never call
 * `gsap.registerPlugin` directly in component code. That keeps plugin
 * registration idempotent and SSR-safe (Vite prerenders 110 routes via
 * jsdom, so anything that touches `window` must be guarded).
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// ─── SSR / browser guard ─────────────────────────────────────────────────────

export const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// ─── prefers-reduced-motion ──────────────────────────────────────────────────

/**
 * Returns `true` when motion is allowed (i.e. user has NOT requested reduced
 * motion). On the server or before the media query is queryable, returns
 * `true` — components are expected to start with their resting/post-animation
 * state and let GSAP take over on mount.
 */
export function motionEnabled(): boolean {
  if (!isBrowser || !window.matchMedia) return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ─── Plugin registration (idempotent) ────────────────────────────────────────

let registered = false;

/**
 * Registers GSAP plugins exactly once per page. Safe to call from many
 * components — second-and-later calls are no-ops.
 *
 * Returns the `gsap` namespace so callers can write
 *   `const gsap = gsapWithScrollTrigger();`
 * without a separate import.
 */
export function gsapWithScrollTrigger(): typeof gsap {
  if (isBrowser && !registered) {
    gsap.registerPlugin(ScrollTrigger);
    registered = true;
  }
  return gsap;
}

/**
 * Re-export ScrollTrigger for components that need to query/refresh it
 * (e.g. on route change).
 */
export { ScrollTrigger };

// ─── Initialise on import (lazy, browser-only) ───────────────────────────────

/**
 * One-shot motion bootstrap. Call from App.tsx or similar top-level mount.
 * Currently just registers plugins, but reserved for global ScrollTrigger
 * defaults, route-change refresh hooks, etc.
 */
export function initMotion(): void {
  gsapWithScrollTrigger();
}

// ─── Tokens — mirror CLAUDE.md motion principles ─────────────────────────────

export const EASE = {
  entrance: 'power2.out',
  exit: 'power2.in',
  loop: 'sine.inOut',
  pop: 'back.out(1.7)',
} as const;

export const DURATION = {
  micro: 0.22,     // 200–300ms band, used for hover lifts / button press
  short: 0.35,     // section reveals — fast end of CLAUDE.md range
  medium: 0.5,     // accordion expand, photo scale
  long: 0.75,      // hero reveal, large entrance
} as const;

export const STAGGER = 0.08; // 80ms — slightly tighter than CLAUDE.md's 150ms
                              // for the section-reveal use case which feels
                              // sluggish at 150ms when 3+ cards are visible.
